import { GeneratorSettings } from '../types';

/** Fonts available in the generator */
export const AVAILABLE_FONTS = [
    { label: 'Dancing Script', value: "'Dancing Script', cursive" },
    { label: 'Plus Jakarta Sans', value: "'Plus Jakarta Sans', sans-serif" },
    { label: 'Outfit', value: "'Outfit', sans-serif" },
    { label: 'Sora', value: "'Sora', sans-serif" },
    { label: 'PT Serif Bold', value: "'PT Serif', serif" },
    { label: 'Inter', value: "'Inter', sans-serif" },
    { label: 'Roboto', value: "'Roboto', sans-serif" },
    { label: 'Playfair Display', value: "'Playfair Display', serif" },
    { label: 'Montserrat', value: "'Montserrat', sans-serif" },
    { label: 'Oswald', value: "'Oswald', sans-serif" },
    { label: 'Raleway', value: "'Raleway', sans-serif" },
    { label: 'Merriweather', value: "'Merriweather', serif" },
    { label: 'Lora', value: "'Lora', serif" },
    { label: 'Nunito', value: "'Nunito', sans-serif" },
    { label: 'Ubuntu', value: "'Ubuntu', sans-serif" },
    { label: 'Poppins', value: "'Poppins', sans-serif" },
    { label: 'Open Sans', value: "'Open Sans', sans-serif" },
    { label: 'Fira Code', value: "'Fira Code', monospace" },
];

/** Wrap text into lines based on a max character-per-line limit */
export const wrapText = (
    ctx: CanvasRenderingContext2D,
    text: string,
    maxWidth: number
): string[] => {
    const words = text.split(' ');
    const lines: string[] = [];
    let currentLine = '';

    for (const word of words) {
        const testLine = currentLine ? `${currentLine} ${word}` : word;
        const metrics = ctx.measureText(testLine);
        if (metrics.width > maxWidth && currentLine !== '') {
            lines.push(currentLine);
            currentLine = word;
        } else {
            currentLine = testLine;
        }
    }
    if (currentLine) lines.push(currentLine);
    return lines;
};

/**
 * Render the quote image onto the canvas.
 */
export const renderCanvas = (
    canvas: HTMLCanvasElement,
    settings: GeneratorSettings,
    isWatermarked: boolean,
    templateImage?: HTMLImageElement | null,
    presetImage?: HTMLImageElement | null
): void => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Use settings width/height
    const W = settings.width || 1080;
    const H = settings.height || 1080;

    // Sync canvas resolution
    if (canvas.width !== W) canvas.width = W;
    if (canvas.height !== H) canvas.height = H;

    // --- 1. Clear and Background ---
    ctx.clearRect(0, 0, W, H);

    if (settings.backgroundType === 'template' && templateImage) {
        // Fit cover calculation
        const canvasAspect = W / H;
        const imgAspect = templateImage.width / templateImage.height;
        let drawW, drawH, drawX, drawY;

        if (imgAspect > canvasAspect) {
            drawH = H;
            drawW = H * imgAspect;
            drawX = (W - drawW) / 2;
            drawY = 0;
        } else {
            drawW = W;
            drawH = W / imgAspect;
            drawX = 0;
            drawY = (H - drawH) / 2;
        }
        ctx.drawImage(templateImage, drawX, drawY, drawW, drawH);

        // semi-transparent overlay for readability
        ctx.fillStyle = 'rgba(0,0,0,0.45)';
        ctx.fillRect(0, 0, W, H);
    } else if (settings.backgroundType === 'preset_image' && presetImage) {
        // Fit cover calculation for preset
        const canvasAspect = W / H;
        const imgAspect = presetImage.width / presetImage.height;
        let drawW, drawH, drawX, drawY;

        if (imgAspect > canvasAspect) {
            drawH = H;
            drawW = H * imgAspect;
            drawX = (W - drawW) / 2;
            drawY = 0;
        } else {
            drawW = W;
            drawH = W / imgAspect;
            drawX = 0;
            drawY = (H - drawH) / 2;
        }
        ctx.drawImage(presetImage, drawX, drawY, drawW, drawH);

        // Overlay for readability
        ctx.fillStyle = 'rgba(0,0,0,0.3)';
        ctx.fillRect(0, 0, W, H);
    } else if (settings.backgroundType === 'gradient' && settings.backgroundGradient?.length >= 2) {
        const gradient = ctx.createLinearGradient(0, 0, W, H);
        gradient.addColorStop(0, settings.backgroundGradient[0]);
        gradient.addColorStop(1, settings.backgroundGradient[1]);
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, W, H);
    } else {
        ctx.fillStyle = settings.backgroundColor;
        ctx.fillRect(0, 0, W, H);
    }

    const padding = W * 0.05; // 5% safe margin (wider content)

    // ─── Shared Username Helper ───────────────────────────────────────────────
    // Used by ALL layouts to respect usernamePosition, font, opacity settings.
    const drawUsername = (prefix = '@') => {
        if (!settings.username) return;
        ctx.save();
        const pos = settings.usernamePosition || 'bottom';
        const uFont = Math.max(settings.fontSize * 0.38, 22);
        ctx.font = `italic 500 ${uFont}px ${settings.font}`;
        ctx.fillStyle = settings.textColor;
        ctx.globalAlpha = 0.85;
        ctx.textBaseline = 'middle';

        let ux = W / 2;
        let uy = H / 2;
        ctx.textAlign = 'center';

        switch (pos) {
            case 'top': ux = W / 2; uy = padding; ctx.textAlign = 'center'; ctx.textBaseline = 'top'; break;
            case 'top-left': ux = padding; uy = padding; ctx.textAlign = 'left'; ctx.textBaseline = 'top'; break;
            case 'top-center': ux = W / 2; uy = padding; ctx.textAlign = 'center'; ctx.textBaseline = 'top'; break;
            case 'top-right': ux = W - padding; uy = padding; ctx.textAlign = 'right'; ctx.textBaseline = 'top'; break;
            case 'bottom': ux = W / 2; uy = H - padding; ctx.textAlign = 'center'; ctx.textBaseline = 'bottom'; break;
            case 'bottom-left': ux = padding; uy = H - padding; ctx.textAlign = 'left'; ctx.textBaseline = 'bottom'; break;
            case 'bottom-center': ux = W / 2; uy = H - padding; ctx.textAlign = 'center'; ctx.textBaseline = 'bottom'; break;
            case 'bottom-right': ux = W - padding; uy = H - padding; ctx.textAlign = 'right'; ctx.textBaseline = 'bottom'; break;
            default: ux = W / 2; uy = H - padding; ctx.textAlign = 'center'; ctx.textBaseline = 'bottom'; break;
        }

        ctx.fillText(`${prefix}${settings.username}`, ux, uy);
        ctx.restore();
    };
    // ─────────────────────────────────────────────────────────────────────────

    // --- SaaS Layout (Progress Bar Style) ---
    if (settings.layout === 'saas') {
        const textColor = settings.textColor;

        // 1. Date row (Top) + username via shared helper
        if (settings.showDateText && settings.dateText) {
            ctx.save();
            ctx.font = `600 ${Math.max(W * 0.024, 24)}px ${settings.font}`;
            ctx.fillStyle = textColor;
            ctx.globalAlpha = 0.5;
            ctx.textAlign = 'right';
            ctx.textBaseline = 'top';
            ctx.fillText(settings.dateText, W - padding, padding);
            ctx.restore();
        }
        drawUsername();

        // 2. Main Content
        ctx.save();
        ctx.font = `800 ${settings.fontSize}px ${settings.font}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = textColor;
        const lines = wrapText(ctx, settings.content, W - padding * 2);
        const lineHeight = settings.fontSize * 1.4;
        const totalHeight = lines.length * lineHeight;
        let startY = H / 2 - totalHeight / 2;
        lines.forEach(line => {
            ctx.fillText(line, W / 2, startY);
            startY += lineHeight;
        });
        ctx.restore();

        // 3. Progress Section (Bottom)
        const barW = W * 0.85;
        const barH = 14;
        const barX = (W - barW) / 2;
        const barY = H * 0.82;

        ctx.save();
        // Label and Value row
        ctx.font = `600 ${Math.max(W * 0.035, 36)}px ${settings.font}`;
        ctx.textAlign = 'left';
        ctx.fillStyle = textColor;
        ctx.globalAlpha = 0.6;
        ctx.fillText('Progress', barX, barY - 45);

        ctx.textAlign = 'right';
        ctx.globalAlpha = 1;
        ctx.font = `800 ${Math.max(W * 0.05, 52)}px ${settings.font}`;
        ctx.fillText(settings.saasCurrentValue || '$0', barX + barW, barY - 45);

        // Track
        ctx.globalAlpha = 0.15;
        ctx.fillStyle = textColor;
        ctx.beginPath();
        ctx.roundRect(barX, barY, barW, barH, 10);
        ctx.fill();

        // Fill
        const progress = settings.saasProgress || 0;
        const fillW = (progress / 100) * barW;
        if (fillW > 0) {
            ctx.globalAlpha = 1;
            const fillGrad = ctx.createLinearGradient(barX, 0, barX + fillW, 0);
            fillGrad.addColorStop(0, '#22c55e');
            fillGrad.addColorStop(1, '#4ade80');
            ctx.fillStyle = fillGrad;
            ctx.beginPath();
            ctx.roundRect(barX, barY, fillW, barH, 10);
            ctx.fill();
        }

        // Percentage
        ctx.font = `600 ${Math.max(W * 0.024, 24)}px ${settings.font}`;
        ctx.textAlign = 'right';
        ctx.globalAlpha = 0.4;
        ctx.fillStyle = textColor;
        ctx.fillText(`${progress}%`, barX + barW, barY + 50);
        ctx.restore();

    } else if (settings.layout === 'saas-hero') {
        const textColor = settings.textColor;

        // 1. Date row (Top right) + Username via shared helper
        if (settings.showDateText && settings.dateText) {
            ctx.save();
            ctx.font = `600 ${Math.max(W * 0.024, 24)}px ${settings.font}`;
            ctx.fillStyle = textColor;
            ctx.globalAlpha = 0.5;
            ctx.textAlign = 'right';
            ctx.textBaseline = 'top';
            ctx.fillText(settings.dateText, W - padding, padding);
            ctx.restore();
        }
        drawUsername();

        // --- THE HIERARCHY ---

        // 2. Headline: "FIRST SALE 🎉"
        const headline = settings.saasDay || 'FIRST SALE 🎉';
        ctx.save();
        const headlineSize = Math.min(W * 0.05, H * 0.08, 54);
        ctx.font = `800 ${headlineSize}px ${settings.font}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = textColor;

        // Subtle glow behind headline
        ctx.shadowColor = textColor;
        ctx.shadowBlur = 30;
        ctx.globalAlpha = 0.9;
        ctx.fillText(headline.toUpperCase(), W / 2, H * 0.22);
        ctx.restore();

        // 3. HERO: The Amount
        const amount = settings.saasCurrentValue || '$29';
        ctx.save();

        // Auto-scaling font size for HERO amount to prevent overflow
        let heroFontSize = Math.min(W * 0.2, H * 0.3, 200);
        ctx.font = `900 ${heroFontSize}px ${settings.font}`;
        const amountWidth = ctx.measureText(amount).width;
        const maxHeroWidth = W * 0.9;

        if (amountWidth > maxHeroWidth) {
            heroFontSize = (maxHeroWidth / amountWidth) * heroFontSize;
            ctx.font = `900 ${heroFontSize}px ${settings.font}`;
        }

        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = textColor;
        // Strong presence
        ctx.shadowColor = 'rgba(0,0,0,0.3)';
        ctx.shadowBlur = 40;
        ctx.shadowOffsetY = 20;
        ctx.fillText(amount, W / 2, H * 0.45);
        ctx.restore();

        // 4. Story / Supporting Text
        const story = settings.content || 'Your startup just made its first sale!';
        ctx.save();
        const storySize = Math.min(W * 0.042, H * 0.06, 44);
        ctx.font = `600 ${storySize}px ${settings.font}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        ctx.fillStyle = textColor;
        ctx.globalAlpha = 0.8;

        const storyLines = wrapText(ctx, story, W - padding * 4);
        let storyY = H * 0.65;
        storyLines.forEach(line => {
            ctx.fillText(line, W / 2, storyY);
            storyY += storySize * 1.5;
        });

        // 5. Decorative Divider
        const dividerY = Math.min(storyY + 40, H * 0.85);
        ctx.globalAlpha = 0.2;
        ctx.strokeStyle = textColor;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(W * 0.4, dividerY);
        ctx.lineTo(W * 0.6, dividerY);
        ctx.stroke();

        // 6. Emotional Context / Footer
        const emotionalContext = settings.saasStartupName || 'The journey begins 🚀';
        if (emotionalContext) {
            ctx.globalAlpha = 0.6;
            ctx.font = `500 ${Math.min(W * 0.032, H * 0.05, 34)}px ${settings.font}`;
            ctx.fillText(emotionalContext, W / 2, dividerY + 60);
        }
        ctx.restore();

        // 7. Milestone Badge
        ctx.save();
        const badgeX = W - padding * 2;
        const badgeY = H - padding * 2;
        ctx.globalAlpha = 0.15;
        ctx.fillStyle = textColor;
        ctx.beginPath();
        ctx.arc(badgeX, badgeY, 60, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 0.4;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.font = `bold 30px ${settings.font}`;
        ctx.fillStyle = textColor;
        ctx.fillText('🏆', badgeX, badgeY);
        ctx.restore();

    } else if (settings.layout === 'liquid-glass') {
        // --- Liquid Glass Layout ---
        const cardW = W * 0.88;
        const cardH = H * 0.75;
        const cardX = (W - cardW) / 2;
        const cardY = (H - cardH) / 2;
        const cardR = 48;

        // 1. Draw blobs only when NOT solid background
        if (settings.backgroundType !== 'solid') {
            ctx.save();
            const drawBlob = (x: number, y: number, r: number, color: string) => {
                const grad = ctx.createRadialGradient(x, y, 0, x, y, r);
                grad.addColorStop(0, color);
                grad.addColorStop(1, 'transparent');
                ctx.fillStyle = grad;
                ctx.beginPath();
                ctx.arc(x, y, r, 0, Math.PI * 2);
                ctx.fill();
            };
            ctx.globalCompositeOperation = 'screen';
            ctx.globalAlpha = 0.6;
            drawBlob(W * 0.2, H * 0.2, W * 0.4, '#ff0080');
            drawBlob(W * 0.8, H * 0.8, W * 0.5, '#7928ca');
            drawBlob(W * 0.8, H * 0.2, W * 0.3, '#4facfe');
            ctx.restore();
        }

        // 2. Glass Card
        ctx.save();
        const glassOverlay = settings.backgroundType === 'solid' ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.08)';
        ctx.fillStyle = glassOverlay;
        ctx.beginPath();
        ctx.roundRect(cardX, cardY, cardW, cardH, cardR);
        ctx.fill();

        // Border (Premium Glass Edge)
        const borderGrad = ctx.createLinearGradient(cardX, cardY, cardX + cardW, cardY + cardH);
        borderGrad.addColorStop(0, 'rgba(255, 255, 255, 0.4)');
        borderGrad.addColorStop(0.5, 'rgba(255, 255, 255, 0.1)');
        borderGrad.addColorStop(1, 'rgba(255, 255, 255, 0.3)');
        ctx.strokeStyle = borderGrad;
        ctx.lineWidth = 3;
        ctx.stroke();

        // 3. Content (respects fontSize, lineHeight, wordWrapLimit)
        ctx.fillStyle = settings.textColor;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        let glassFontSize = settings.fontSize;
        const glassLineHeight = settings.lineHeight || 1.3;

        // Apply word wrap limit
        let glassContent = settings.content;
        if (settings.wordWrapLimit > 0) {
            const words = settings.content.split(' ');
            const limited: string[] = [];
            let ln = '';
            for (const word of words) {
                if ((ln + ' ' + word).trimStart().length > settings.wordWrapLimit && ln !== '') {
                    limited.push(ln.trim());
                    ln = word;
                } else {
                    ln = ln ? `${ln} ${word}` : word;
                }
            }
            if (ln) limited.push(ln.trim());
            glassContent = limited.join('\n');
        }

        const glassRawLines = glassContent.split('\n');
        ctx.font = `800 ${glassFontSize}px ${settings.font}`;
        const glassLines: string[] = [];
        for (const raw of glassRawLines) {
            glassLines.push(...wrapText(ctx, raw, cardW - padding * 3));
        }

        const glassLineHeightPx = glassFontSize * glassLineHeight;
        let totalHeight = glassLines.length * glassLineHeightPx;

        // Auto-scale if too many lines for the card
        if (totalHeight > cardH * 0.65) {
            glassFontSize = (cardH * 0.65 / totalHeight) * glassFontSize;
            ctx.font = `800 ${glassFontSize}px ${settings.font}`;
            totalHeight = glassLines.length * (glassFontSize * glassLineHeight);
        }

        let startY = H / 2 - totalHeight / 2 + (glassFontSize * glassLineHeight) / 2;

        glassLines.forEach(line => {
            ctx.fillText(line, W / 2, startY);
            startY += glassFontSize * glassLineHeight;
        });

        ctx.restore();

        // 4. Username — respects usernamePosition
        drawUsername();

    } else if (settings.layout === 'celebration') {
        // --- Celebration Layout (Milestones) ---

        // 1. Decorative background elements (Confetti/Stars)
        ctx.save();
        const colors = ['#fbbf24', '#f59e0b', '#d97706', '#fef3c7']; // Golden shades

        // Use a pseudo-random seed based on the layout name so stars don't jump while typing
        let seed = 123;
        const pseudoRandom = () => {
            seed = (seed * 9301 + 49297) % 233280;
            return seed / 233280;
        };

        for (let i = 0; i < 60; i++) {
            ctx.fillStyle = colors[Math.floor(pseudoRandom() * colors.length)];
            ctx.globalAlpha = pseudoRandom() * 0.6 + 0.2;
            const size = pseudoRandom() * 12 + 4;
            const x = pseudoRandom() * W;
            const y = pseudoRandom() * H;

            // Concentrate some at the bottom corners
            let finalX = x;
            let finalY = y;
            if (i > 40) {
                const side = pseudoRandom() > 0.5 ? 1 : 0;
                finalX = side === 0 ? pseudoRandom() * W * 0.3 : W - pseudoRandom() * W * 0.3;
                finalY = H - pseudoRandom() * H * 0.25;
            }

            ctx.save();
            ctx.translate(finalX, finalY);
            ctx.rotate(pseudoRandom() * Math.PI);

            if (pseudoRandom() > 0.4) {
                ctx.beginPath();
                ctx.arc(0, 0, size / 2, 0, Math.PI * 2);
                ctx.fill();
            } else {
                ctx.beginPath();
                for (let j = 0; j < 5; j++) {
                    ctx.lineTo(Math.cos((18 + j * 72) * Math.PI / 180) * size,
                        Math.sin((18 + j * 72) * Math.PI / 180) * size);
                    ctx.lineTo(Math.cos((54 + j * 72) * Math.PI / 180) * (size / 2),
                        Math.sin((54 + j * 72) * Math.PI / 180) * (size / 2));
                }
                ctx.closePath();
                ctx.fill();
            }
            ctx.restore();
        }
        ctx.restore();

        // 2. Parse Content (Multi-line support)
        const lines = (settings.content || '').split('\n').map(l => l.trim()).filter(Boolean);

        // 3. Username — rendered in cursive script, uses shared position logic
        if (settings.username) {
            ctx.save();
            const pos = settings.usernamePosition || 'top';
            const uFont = Math.max(W * 0.04, 36);
            ctx.font = `500 ${uFont}px 'Dancing Script', cursive`;
            ctx.fillStyle = settings.textColor;
            ctx.globalAlpha = 0.85;

            let ux = W / 2, uy = padding;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'top';

            switch (pos) {
                case 'top-left': ux = padding; ctx.textAlign = 'left'; break;
                case 'top-right': ux = W - padding; ctx.textAlign = 'right'; break;
                case 'top-center': ctx.textAlign = 'center'; break;
                case 'bottom': ux = W / 2; uy = H - padding; ctx.textAlign = 'center'; ctx.textBaseline = 'bottom'; break;
                case 'bottom-left': ux = padding; uy = H - padding; ctx.textAlign = 'left'; ctx.textBaseline = 'bottom'; break;
                case 'bottom-center': uy = H - padding; ctx.textAlign = 'center'; ctx.textBaseline = 'bottom'; break;
                case 'bottom-right': ux = W - padding; uy = H - padding; ctx.textAlign = 'right'; ctx.textBaseline = 'bottom'; break;
                default: break;
            }

            ctx.fillText(settings.username, ux, uy);
            ctx.restore();
        }

        // Horizontal center for main content
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        let currentY = H * 0.38;

        // Helper to format large numbers (e.g., 1000 -> 1k, 1000000 -> 1m)
        const formatMilestone = (val: string) => {
            const num = parseFloat(val.replace(/[^0-9.]/g, ''));
            if (isNaN(num)) return val;
            if (num >= 1000000) return (num / 1000000).toFixed(num % 1000000 === 0 ? 0 : 1) + 'M';
            if (num >= 1000) return (num / 1000).toFixed(num % 1000 === 0 ? 0 : 1) + 'K';
            return val;
        };

        const milestoneBase = settings.milestoneCount || '0';
        const milestoneText = formatMilestone(milestoneBase);

        // GIANT NUMBER (Balloon-ish)
        ctx.save();
        let milestoneFontSize = Math.min(W * 0.3, H * 0.4, 300);
        ctx.font = `900 ${milestoneFontSize}px ${settings.font}`;

        // Final sanity check for width in short formats
        const mWidth = ctx.measureText(milestoneText).width;
        if (mWidth > W * 0.9) {
            milestoneFontSize = (W * 0.9 / mWidth) * milestoneFontSize;
            ctx.font = `900 ${milestoneFontSize}px ${settings.font}`;
        }

        const mColor = settings.milestoneColor || settings.textColor;
        const grad = ctx.createLinearGradient(0, currentY - 100, 0, currentY + 100);
        grad.addColorStop(0, mColor);
        grad.addColorStop(1, '#000000');
        ctx.fillStyle = grad;
        ctx.shadowColor = 'rgba(0,0,0,0.3)';
        ctx.shadowBlur = 40;
        ctx.shadowOffsetY = 20;
        ctx.fillText(milestoneText, W / 2, currentY);
        ctx.restore();

        // Remaining lines
        const remainingLines = settings.milestoneCount ? lines : lines.slice(1);

        remainingLines.forEach((line, idx) => {
            ctx.save();
            const actualIdx = settings.milestoneCount ? idx + 1 : idx + 2;

            if (actualIdx === 1) {
                // FOLLOWER Label (Positioned clearly below the giant number)
                const labelSize = Math.min(W * 0.06, H * 0.09, 60);
                const labelY = currentY + (milestoneFontSize * 0.45) + (labelSize * 0.5);
                ctx.font = `800 ${labelSize}px ${settings.font}`;
                ctx.fillStyle = settings.textColor;
                ctx.globalAlpha = 0.9;
                (ctx as any).letterSpacing = '10px';
                ctx.fillText(line.toUpperCase(), W / 2, labelY);
                currentY = labelY + labelSize * 0.5;
            } else if (actualIdx === 2) {
                // "Thank you" (Script)
                const scriptSize = Math.min(W * 0.1, H * 0.15, 110);
                const scriptY = currentY + (scriptSize * 0.8);
                ctx.font = `600 ${scriptSize}px 'Dancing Script', cursive`;
                ctx.fillStyle = settings.textColor;
                ctx.fillText(line, W / 2, scriptY);
                currentY = scriptY + scriptSize * 0.5;
            } else {
                // Subtext
                const subtextSize = Math.min(W * 0.032, H * 0.06, 34);
                const subtextY = currentY + (subtextSize * 1.2);
                ctx.font = `500 ${subtextSize}px ${settings.font}`;
                ctx.fillStyle = settings.textColor;
                ctx.globalAlpha = 0.7;
                ctx.fillText(line, W / 2, subtextY);
                currentY = subtextY + subtextSize * 0.5;
            }
            ctx.restore();
        });

        // Stats rendering removed from Milestone layout as per user request

    } else if (settings.layout === 'stats') {
        ctx.fillStyle = settings.textColor;

        // 1. Username (uses shared drawUsername helper)
        drawUsername();

        // 2. Date (Top Right)
        if (settings.showDateText && settings.dateText) {
            ctx.font = `400 ${Math.max(W * 0.022, 20)}px ${settings.font}`;
            ctx.textAlign = 'right';
            ctx.fillText(settings.dateText, W - padding, padding);
        }

        // 3. Stats Title (Large Center) — uses content field
        const statsTitle = settings.content || settings.statsTitle || 'Stats';
        ctx.font = `800 ${settings.fontSize}px ${settings.font}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.globalAlpha = 1;

        // Apply word wrap limit (characters)
        let processedStatsTitle = statsTitle;
        if (settings.wordWrapLimit > 0) {
            const words = statsTitle.split(' ');
            const limited: string[] = [];
            let line = '';
            for (const word of words) {
                if ((line + ' ' + word).trimStart().length > settings.wordWrapLimit && line !== '') {
                    limited.push(line.trim());
                    line = word;
                } else {
                    line = line ? `${line} ${word}` : word;
                }
            }
            if (line) limited.push(line.trim());
            processedStatsTitle = limited.join('\n');
        }

        const rawLines = processedStatsTitle.split('\n');
        const wrappedStatsTitle: string[] = [];
        for (const raw of rawLines) {
            wrappedStatsTitle.push(...wrapText(ctx, raw, W - padding * 2));
        }

        const statsLineHeight = settings.fontSize * settings.lineHeight;
        let statsY = H * 0.42 - (wrappedStatsTitle.length - 1) * (statsLineHeight / 2);

        wrappedStatsTitle.forEach(line => {
            ctx.fillText(line, W / 2, statsY);
            statsY += statsLineHeight;
        });

        // 4. Stats Items Grid (Bottom Section)
        if (settings.stats && settings.stats.length > 0) {
            // Make items position relative to title bottom to avoid overlap
            let startY = Math.max(H * 0.72, statsY + 40);
            const itemSpacing = Math.max(H * 0.05, 50);

            settings.stats.forEach((item, idx) => {
                ctx.textAlign = 'center';

                // Icon + Value + Label
                const icon = item.icon || '👤';
                const displayText = `${icon} ${item.value} ${item.label}`;

                // First item is larger and bold
                if (idx === 0) {
                    ctx.font = `700 ${Math.min(W * 0.045, H * 0.07, 42)}px ${settings.font}`;
                } else {
                    ctx.font = `500 ${Math.min(W * 0.028, H * 0.05, 28)}px ${settings.font}`;
                    ctx.globalAlpha = 0.8;
                }

                ctx.fillText(displayText, W / 2, startY);
                startY += itemSpacing;
            });
        }

        // 5. Logo (Bottom Right)
        if (settings.showLogo) {
            // Logo placeholder
        }

        ctx.globalAlpha = 1;
    } else {
        // --- Default Layout ---
        ctx.fillStyle = settings.textColor;

        // 0. Date Rendering (Top Right)
        if (settings.showDateText && settings.dateText) {
            ctx.font = `500 ${Math.max(W * 0.022, 20)}px ${settings.font}`;
            ctx.textAlign = 'right';
            ctx.textBaseline = 'top';
            ctx.globalAlpha = 0.5;
            ctx.fillText(settings.dateText, W - padding, padding);
            ctx.globalAlpha = 1;
        }

        const maxTextWidth = W - padding * 2;
        const lineHeightPx = settings.fontSize * settings.lineHeight;

        ctx.fillStyle = settings.textColor;
        ctx.font = `${settings.fontSize}px ${settings.font}`;
        ctx.textBaseline = 'top';

        // Alignment setup
        ctx.textAlign = settings.textAlign || 'center';

        // Apply word wrap limit (characters)
        let processedContent = settings.content;
        if (settings.wordWrapLimit > 0) {
            const words = settings.content.split(' ');
            const limited: string[] = [];
            let line = '';
            for (const word of words) {
                if ((line + ' ' + word).trimStart().length > settings.wordWrapLimit && line !== '') {
                    limited.push(line.trim());
                    line = word;
                } else {
                    line = line ? `${line} ${word}` : word;
                }
            }
            if (line) limited.push(line.trim());
            processedContent = limited.join('\n');
        }

        const rawLines = processedContent.split('\n');
        const allLines: string[] = [];
        for (const raw of rawLines) {
            const wrapped = wrapText(ctx, raw, maxTextWidth);
            allLines.push(...wrapped);
        }

        const totalTextHeight = allLines.length * lineHeightPx;
        // Auto center quote vertically
        let y = (H - totalTextHeight) / 2;

        for (const line of allLines) {
            let x = W / 2; // Center default
            if (settings.textAlign === 'left') x = padding;
            if (settings.textAlign === 'right') x = W - padding;

            ctx.fillText(line, x, y);
            y += lineHeightPx;
        }

        // --- Username (Customizable Position) ---
        if (settings.username) {
            const pos = settings.usernamePosition || 'bottom';
            const usernameText = pos === 'below-content' ? `— @${settings.username}` : `@${settings.username}`;
            const usernameFont = Math.max(settings.fontSize * 0.45, 20);
            ctx.font = `italic 500 ${usernameFont}px ${settings.font}`;
            ctx.fillStyle = settings.textColor;
            ctx.globalAlpha = 0.8;

            let ux = W / 2;
            let uy = y + 20; // Default below content
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';

            switch (pos) {
                case 'top':
                    ux = W / 2;
                    uy = padding;
                    ctx.textBaseline = 'top';
                    break;
                case 'bottom':
                    ux = W / 2;
                    uy = H - padding;
                    ctx.textBaseline = 'bottom';
                    break;
                case 'top-left':
                    ux = padding;
                    uy = padding;
                    ctx.textAlign = 'left';
                    ctx.textBaseline = 'top';
                    break;
                case 'top-right':
                    ux = W - padding;
                    uy = padding;
                    ctx.textAlign = 'right';
                    ctx.textBaseline = 'top';
                    break;
                case 'top-center':
                    ux = W / 2;
                    uy = padding;
                    ctx.textBaseline = 'top';
                    break;
                case 'bottom-left':
                    ux = padding;
                    uy = H - padding;
                    ctx.textAlign = 'left';
                    ctx.textBaseline = 'bottom';
                    break;
                case 'bottom-center':
                    ux = W / 2;
                    uy = H - padding;
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'bottom';
                    break;
                case 'bottom-right':
                    ux = W - padding;
                    uy = H - padding;
                    ctx.textAlign = 'right';
                    ctx.textBaseline = 'bottom';
                    break;
                case 'below-content':
                default:
                    ux = settings.textAlign === 'left' ? padding : (settings.textAlign === 'right' ? W - padding : W / 2);
                    uy = y + 20;
                    ctx.textAlign = settings.textAlign || 'center';
                    ctx.textBaseline = 'top';
                    break;
            }

            ctx.fillText(usernameText, ux, uy);
            ctx.globalAlpha = 1;
        }
    }

    // --- Watermark (free plan) ---
    if (isWatermarked) {
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        ctx.font = `600 ${Math.max(W * 0.015, 14)}px 'Plus Jakarta Sans', sans-serif`;
        ctx.fillStyle = 'rgba(255,255,255,0.4)';
        ctx.fillText('Created with Share Visuals', W / 2, H - 20);
    }
};

/** Convert a canvas to a Blob */
export const canvasToBlob = (canvas: HTMLCanvasElement): Promise<Blob> => {
    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (blob) resolve(blob);
            else reject(new Error('Canvas toBlob failed'));
        }, 'image/png');
    });
};
